<script>
import BaseCheckbox from '@/components/_base-checkbox.vue'
import BaseDropdownMenu from '@/components/_base-dropdown-menu.vue'
import BookingOptionsSheet from '@/components/booking/bookingList/booking-row-column/booking-options-sheet.vue'
import DateColumn from '@/components/booking/bookingList/booking-row-column/date-column.vue'
import EducatorColumn from '@/components/booking/bookingList/booking-row-column/educator-column.vue'
import HoverClickPopup from '@/components/booking/bookingList/hover-click-popup.vue'
import UnapprovedBookingButtons from '@/components/booking/bookingList/unapproved-booking-buttons.vue'
import BookingFeedbackModal from '@/components/booking/feedback/booking-feedback-modal.vue'
import baseMixin from '@/mixins/baseMixin'
import featureFlagMixin from '@/mixins/featureFlagMixin'
import EmitEvent from '@/models/general/emitEvent'
import Interaction from '@/models/general/interaction'
import MenuItem from '@/models/general/menuItem'
import dayjs from '@/services/date'
import BookingCard from '@/shared/constants/booking/BookingCard'
import BookingListTabKey from '@/shared/constants/booking/BookingListTabKey'
import BookingStatusLabelType from '@/shared/constants/booking/BookingStatusLabelType'
import BookingType from '@/shared/constants/booking/BookingType'
import InteractionType from '@/shared/constants/core/InteractionType'
import VuetifyColorClass from '@/shared/constants/core/VuetifyColourClass'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import routeDefinitions from '@/shared/constants/routes/routeDefinitions'
import BookingStatusLabel from '@components/booking/booking-status-label.vue'
import CustomTooltip from '@components/booking/bookingList/custom-tooltip.vue'
import CancelBookingDialog from '@components/booking/cancel-booking-dialog.vue'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'BookingRow',
  components: {
    CustomTooltip,
    BaseCheckbox,
    HoverClickPopup,
    BookingFeedbackModal,
    CancelBookingDialog,
    BaseDropdownMenu,
    BookingStatusLabel,
    DateColumn,
    EducatorColumn,
    BookingOptionsSheet,
    UnapprovedBookingButtons,
  },
  mixins: [baseMixin, featureFlagMixin],
  props: {
    booking: { type: Object, required: true },
    selected: { type: Boolean, default: false },
    showNotes: { type: Boolean, default: false },
    activeTab: { type: [String, Number], default: '' },
    currentTabName: { type: String, default: '' },
  },
  data: () => {
    return {
      isBookingOptions: false,
      feedbackDialog: false,
      quickFeedbackSuccess: false,
      bookingToCancel: null,
      showCancelBookingDialog: false,
      BookingStatusLabelType,
      BookingType,
      BookingListTabKey,
    }
  },
  computed: {
    ...mapGetters({
      isSubmittingQuickFeedback: 'bookings/isSubmittingQuickFeedback',
      getSelectedClients: 'client/getSelectedClients',
      hasCreateBookingPermissionForAtleastOneClientLocationByClientIds:
        'auth/hasCreateBookingPermissionForAtleastOneClientLocationByClientIds',
      hasLocationBookingCreatePermission:
        'auth/hasLocationBookingCreatePermission',
      hasLocationPendingApprovalViewPermission:
        'auth/hasLocationPendingApprovalViewPermission',
    }),
    isDayByDay() {
      return !!this.activeTab
    },
    blockRange() {
      // if day-by-day, no need for block range
      if (this.isDayByDay) return null

      // if no blockDetails, fallback to booking start/end
      if (
        !Array.isArray(this.booking.blockDetails) ||
        !this.booking.blockDetails.length
      ) {
        if (this.booking.startDateTimeLocal && this.booking.endDateTimeLocal) {
          return {
            minStart: new Date(this.booking.startDateTimeLocal),
            maxEnd: new Date(this.booking.endDateTimeLocal),
          }
        }
        return null
      }

      // collect blockDetails normally
      const starts = this.booking.blockDetails
        .map((b) => new Date(b.startDateTimeLocal))
        .filter((d) => !isNaN(d))
      const ends = this.booking.blockDetails
        .map((b) => new Date(b.endDateTimeLocal))
        .filter((d) => !isNaN(d))
      if (!starts.length || !ends.length) return null

      const minStart = new Date(Math.min(...starts))
      const maxEnd = new Date(Math.max(...ends))
      return { minStart, maxEnd }
    },
    bookingTime() {
      return (
        dayjs(this.booking.startDateTimeLocal).format(DateFormatToken.HHmm) +
        ' - ' +
        dayjs(this.booking.endDateTimeLocal).format(DateFormatToken.HHmm)
      )
    },
    blockTimes() {
      // if day-by-day, no need for block range
      if (this.isDayByDay) return null

      // fallback if no blockDetails → use booking start/end
      if (
        !Array.isArray(this.booking.blockDetails) ||
        !this.booking.blockDetails.length
      ) {
        if (this.booking.startDateTimeLocal && this.booking.endDateTimeLocal) {
          const fmt = (d) => dayjs(d).format(DateFormatToken.HHmm)

          return {
            vary: false,
            start: fmt(this.booking.startDateTimeLocal),
            end: fmt(this.booking.endDateTimeLocal),
          }
        }
        return null
      }

      // handle blockDetails normally
      const fmt = (d) => dayjs(d).format(DateFormatToken.HHmm)

      const starts = this.booking.blockDetails.map((b) =>
        fmt(b.startDateTimeLocal)
      )
      const ends = this.booking.blockDetails.map((b) => fmt(b.endDateTimeLocal))

      const allStartSame = starts.every((t) => t === starts[0])
      const allEndSame = ends.every((t) => t === ends[0])

      if (allStartSame && allEndSame) {
        return { vary: false, start: starts[0], end: ends[0] }
      }

      const pairs = [
        ...new Set(
          this.booking.blockDetails.map(
            (b) => `${fmt(b.startDateTimeLocal)} - ${fmt(b.endDateTimeLocal)}`
          )
        ),
      ]

      return { vary: true, variations: pairs.join(', ') }
    },
    bookingId() {
      return this.booking?.id
    },
    feedback() {
      return this.booking?.feedback
    },
    feedbackProvided() {
      return this.feedback.provided || this.quickFeedbackSuccess
    },
    isUnfilled() {
      return this.booking.statusLabel === BookingCard.labelStatus.InProgress
    },
    canCreateBookings() {
      return this.hasCreateBookingPermissionForAtleastOneClientLocationByClientIds(
        this.getSelectedClients
      )
    },
    canApprovePendingBooking() {
      return (
        this.hasLocationBookingCreatePermission(this.booking.location.id) &&
        this.hasLocationPendingApprovalViewPermission(this.booking.location.id)
      )
    },
    buttonConfig() {
      const {
        warning200,
        blueBgHover,
        warning,
        blue,
        grey500,
      } = VuetifyColorClass
      return {
        hoverColor: this.isUnfilled ? warning200 : blueBgHover,
        iconColor: grey500,
        iconHoverColor: this.isUnfilled ? warning : blue,
      }
    },
    isPastBooking() {
      if (this.blockRange) {
        return (
          dayjs(this.blockRange.minStart) <
          dayjs().tz(this.booking.location.timeZone)
        )
      }
      return (
        dayjs(this.booking.startDateTimeLocal) <
        dayjs().tz(this.booking.location.timeZone)
      )
    },
    isUpcomingBooking() {
      return (
        !this.isPastBooking &&
        this.booking.statusLabel === BookingStatusLabelType.Open
      )
    },
    dropdownItems() {
      if (this.currentTabName === BookingListTabKey.upcoming) {
        const options = this.isUpcomingBooking ? this.cardOptions : []
        return [
          ...this.editBookingOptions,
          ...this.duplicateBookingOptions,
          ...options,
        ]
      } else if (this.currentTabName === BookingListTabKey.past) {
        const past = this.feedbackCardOptions
        return [...this.duplicateBookingOptions, ...past]
      } else if (this.currentTabName === BookingListTabKey.all) {
        const upcoming = this.isUpcomingBooking ? this.cardOptions : []
        const past = this.isPastBooking ? this.feedbackCardOptions : []
        return [
          ...this.editBookingOptions,
          ...this.duplicateBookingOptions,
          ...upcoming,
          ...past,
        ]
      }
      return []
    },
    cardOptions() {
      const options = [
        {
          icon: 'mdi-close-circle-outline',
          type: 'action',
          text: this.$t('booking.cancelBookingBtn'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'cancelBooking',
              value: this.booking.id,
            }),
          }),
          rules: [this.canCreateBookings],
        },
      ]

      const filteredOptions = options.filter((option) =>
        option.rules.every((rule) => rule)
      )

      return filteredOptions.map((option) => new MenuItem(option))
    },
    feedbackCardOptions() {
      const options = [
        {
          icon: 'mdi-message-alert-outline',
          type: 'action',
          text: this.$t('feedback.provideFeedbackBtn'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'showFeedback',
              value: this.booking.id,
            }),
          }),
        },
      ]

      return options.map((option) => new MenuItem(option))
    },
    editBookingOptions() {
      const isToday =
        dayjs(this.booking.startDateTimeLocal).format(
          DateFormatToken.YYYYMMDD
        ) ===
        dayjs()
          .tz(this.booking.location.timeZone)
          .format(DateFormatToken.YYYYMMDD)

      const allBlocksOpenAndFuture =
        this.booking.blockDetails &&
        Array.isArray(this.booking.blockDetails) &&
        this.booking.blockDetails.every(
          (block) =>
            block.statusLabel === BookingStatusLabelType.Open &&
            dayjs(block.startDateTimeLocal).format(DateFormatToken.YYYYMMDD) >
              dayjs()
                .tz(this.booking.location.timeZone)
                .format(DateFormatToken.YYYYMMDD)
        )

      const bookingCount = this.booking.blockDetails
        ? this.booking.blockDetails.length
        : 0

      const options = [
        {
          id: isToday
            ? 'edit-today-booking-menu-item'
            : 'edit-day-booking-menu-item',
          icon: isToday ? 'mdi-pencil-outline' : 'mdi-calendar-edit-outline',
          type: 'action',
          text: isToday
            ? this.$t('booking.edit')
            : this.$t('booking.editBookingDay'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'editBookingDay',
              value: this.booking.id,
            }),
          }),
          rules: [
            this.selfServiceFeatureFlags.enableEditBooking,
            this.canCreateBookings,
            this.isDayByDay,
            this.booking.statusLabel === BookingStatusLabelType.Open,
            dayjs(this.booking.startDateTimeLocal).format(
              DateFormatToken.YYYYMMDD
            ) >=
              dayjs()
                .tz(this.booking.location.timeZone)
                .format(DateFormatToken.YYYYMMDD),
          ],
          data: {
            disabled: isToday,
          },
        },
        {
          icon: 'mdi-pencil-outline',
          type: 'action',
          text: this.$t('booking.editBookingBlock'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'editBookingBlock',
              value: this.booking.id,
            }),
          }),
          rules: [
            this.selfServiceFeatureFlags.enableEditBooking,
            this.canCreateBookings,
            this.booking.statusLabel === BookingStatusLabelType.Open,
            allBlocksOpenAndFuture,
            bookingCount === this.booking.openCount,
          ],
        },
      ]

      const filteredOptions = options.filter((option) =>
        option.rules.every((rule) => rule)
      )

      return filteredOptions.map((option) => new MenuItem(option))
    },
    isValidDuplicateStatus() {
      const validStatuses = [
        BookingStatusLabelType.Open,
        BookingStatusLabelType.Pending,
        BookingStatusLabelType.Unapproved,
        BookingStatusLabelType.Confirmed,
      ]

      let isValidStatus = validStatuses.includes(this.booking.statusLabel)

      if (!this.isDayByDay) {
        const blockDetails = this.booking.blockDetails
        const hasBlocks = Array.isArray(blockDetails) && blockDetails.length > 0
        if (hasBlocks) {
          isValidStatus = this.booking.blockDetails.every((block) =>
            validStatuses.includes(block.statusLabel)
          )
        }
      }

      return isValidStatus
    },
    duplicateBookingOptions() {
      const options = [
        {
          id: 'duplicate-booking-menu-item',
          icon: 'mdi-content-copy',
          type: 'action',
          text: this.$t('booking.duplicate'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'duplicateBooking',
              value: this.booking.id,
            }),
          }),
          rules: [
            this.selfServiceFeatureFlags.enableDuplicateBooking,
            this.canCreateBookings,
          ],
          data: {
            disabled: !this.isValidDuplicateStatus,
          },
        },
      ]

      const filteredOptions = options.filter((option) =>
        option.rules.every((rule) => rule)
      )

      return filteredOptions.map((option) => new MenuItem(option))
    },
    displayClientNotesAction() {
      const clientNotes = this.booking.clientNotes
      return clientNotes && clientNotes.trim()
    },
  },
  methods: {
    ...mapActions({
      requestFeedbackFormUrl: 'bookings/requestFeedbackFormUrl',
      submitQuickFeedback: 'bookings/submitQuickFeedback',
    }),
    viewCandidate(candidate) {
      if (candidate && candidate.id != null) {
        this.$emit('viewCandidate', candidate.id)
      }
    },
    updateShowNotes(val) {
      this.$emit('update-show-notes', val)
    },
    viewSidepanel(bookingDetails) {
      this.$emit('sidePanel', bookingDetails)
    },
    openBookingOptions() {
      if (
        this.booking.statusLabel === BookingStatusLabelType.Open &&
        (this.currentTabName === BookingListTabKey.upcoming ||
          this.currentTabName === BookingListTabKey.all)
      ) {
        this.isBookingOptions = true
      } else if (
        this.currentTabName === BookingListTabKey.past ||
        (this.currentTabName === BookingListTabKey.all && this.isPastBooking)
      ) {
        this.isBookingOptions = true
      } else {
        this.isBookingOptions = false
      }
    },
    openFeedback() {
      this.feedbackDialog = true
    },
    handleFeedbackProvided() {
      this.quickFeedbackSuccess = true
    },
    openCancelBookingDialog() {
      this.bookingToCancel = this.booking
      this.showCancelBookingDialog = true
    },
    closeCancelBookingDialog() {
      this.bookingToCancel = null
      this.showCancelBookingDialog = false
    },
    async closeCancelBookingDialogAndRefresh() {
      this.closeCancelBookingDialog()
      await this.refreshDashboardBookingData()
    },
    reloadBookingList() {
      this.$emit('reloadBookingList')
    },
    disableMenuItem(menuItem) {
      if (!menuItem) return false

      switch (menuItem.id) {
        case 'edit-today-booking-menu-item':
        case 'duplicate-booking-menu-item':
          return menuItem.data.disabled
        default:
          return false
      }
    },
    editBookingDay() {
      this.$router.push({
        name: routeDefinitions.bookingsEdit.name,
        params: { bookingId: this.booking.id },
        query: {
          mode: BookingType.SingleDay,
          date: dayjs(this.booking.startDateTimeLocal).format(
            DateFormatToken.YYYYMMDD
          ),
        },
      })
    },
    editBookingBlock() {
      this.$router.push({
        name: routeDefinitions.bookingsEdit.name,
        params: { bookingId: this.booking.id },
        query: {
          mode: BookingType.Block,
        },
      })
    },
    duplicateBooking() {
      const bookingMode = this.isDayByDay
        ? BookingType.SingleDay
        : BookingType.Block

      const query = {
        mode: bookingMode,
      }

      if (this.isDayByDay) {
        query.date = dayjs(this.booking.startDateTimeLocal).format(
          DateFormatToken.YYYYMMDD
        )
      }

      this.$router.push({
        name: routeDefinitions.bookingsDuplicate.name,
        params: { bookingId: this.booking.id },
        query,
      })
    },
  },
}
</script>

<template>
  <tr class="booking-row" :class="{ 'selected-row': selected }">
    <!-- Checkbox (mobile) -->
    <td class="checkbox-cell">
      <CustomTooltip
        position="right"
        :text="
          selected
            ? $t('customeToolTip.unselectBooking')
            : $t('customeToolTip.selectBooking')
        "
      >
        <BaseCheckbox
          md
          class="custom-checkbox"
          :model-value="selected"
          @update:modelValue="$emit('toggle-select')"
        />
      </CustomTooltip>
    </td>

    <DateColumn
      ref="dateColumn"
      :booking="booking"
      :current-tab-name="currentTabName"
      :active-tab="activeTab"
      :booking-time="bookingTime"
      :is-day-by-day="isDayByDay"
      :block-range="blockRange"
      :block-times="blockTimes"
      :view-sidepanel="viewSidepanel"
    />

    <!-- DAYS column (only when not day-by-day OR approval) -->
    <td
      v-if="
        !isMobile &&
          (!activeTab || currentTabName === BookingListTabKey.approvalRequired)
      "
      @click.stop="viewSidepanel(booking)"
    >
      <span class="days-count">{{ booking.days }}</span>
      <span v-if="booking.openCount && booking.openCount > 0" class="open-days">
        {{ booking.openCount }} {{ $t('general.open') }}
      </span>
    </td>

    <EducatorColumn
      ref="educatorColumn"
      :booking="booking"
      :current-tab-name="currentTabName"
      :is-day-by-day="isDayByDay"
      :booking-status-label-type="BookingStatusLabelType"
      :view-sidepanel="viewSidepanel"
      @viewCandidate="viewCandidate"
    />

    <!-- TIME (desktop) -->
    <td v-if="!isMobile" @click.stop="viewSidepanel(booking)">
      <template v-if="isDayByDay">
        {{ bookingTime }}
      </template>
      <template v-else-if="blockRange">
        <template v-if="booking.timeVaries">
          <div class="times-vary">
            <CustomTooltip :text="blockTimes.variations">{{
              $t('bookingList.timesVary')
            }}</CustomTooltip>
          </div>
        </template>
        <template v-else
          >{{ blockTimes.start }} - {{ blockTimes.end }}</template
        >
      </template>
    </td>

    <!-- STATUS (mobile) -->
    <td
      v-if="
        (isMobile || (isDayByDay && !isMobile)) &&
          currentTabName !== BookingListTabKey.approvalRequired &&
          currentTabName !== BookingListTabKey.cancelled
      "
      @click.stop="viewSidepanel(booking)"
    >
      <BookingStatusLabel
        :class="['status-cell status-label', booking.statusLabel.toLowerCase()]"
        :status="booking.statusLabel"
      />
    </td>

    <!-- CLASSIFICATION / ROOM / REPLACING / GRADE / CERTS (desktop) -->
    <td
      v-if="currentTabName === BookingListTabKey.approvalRequired"
      @click.stop="viewSidepanel(booking)"
    >
      {{ booking.classification.name }}
    </td>
    <td v-else-if="!isMobile" @click.stop="viewSidepanel(booking)">
      {{ booking.classification.name }}
    </td>
    <td v-if="!isMobile" @click.stop="viewSidepanel(booking)">
      {{ booking.roomNumber ? booking.roomNumber : '—' }}
    </td>
    <td v-if="!isMobile" @click.stop="viewSidepanel(booking)">
      {{
        booking.replacing && booking.replacing.firstName
          ? booking.replacing.firstName
          : ''
      }}
      {{
        booking.replacing && booking.replacing.lastName
          ? booking.replacing.lastName
          : ''
      }}
    </td>
    <td
      v-if="!isMobile"
      class="text-no-wrap"
      @click.stop="viewSidepanel(booking)"
    >
      {{
        booking.grades && booking.grades.length
          ? booking.grades.map((g) => g.title).join(', ')
          : '—'
      }}
    </td>
    <td
      v-if="!isMobile"
      class="small-cell d-flex fill-height"
      @click.stop="viewSidepanel(booking)"
    >
      <BaseChip
        v-for="(certification, index) in booking.certificationTypes"
        :key="`certificate-${index}`"
        :variant="index % 2 === 0 ? 'blue' : 'purple'"
        :title="certification"
      />
    </td>

    <!-- CLIENT column when approval/cancelled -->
    <td
      v-if="
        !isMobile &&
          (currentTabName === BookingListTabKey.approvalRequired ||
            currentTabName === BookingListTabKey.cancelled)
      "
      class="text-no-wrap"
      @click.stop="viewSidepanel(booking)"
    >
      {{ booking.client ? booking.client.name : '—' }}
    </td>

    <!-- Actions / notes (desktop) -->
    <td
      v-if="!isMobile && currentTabName !== BookingListTabKey.approvalRequired"
      style="text-align: end"
      class="notes-col"
      :class="[{ 'show-notes': showNotes }, { 'selected-row': selected }]"
    >
      <div
        class="d-flex notes-cell"
        :class="{
          'no-dropdown': dropdownItems.length === 0,
          'no-notes': !displayClientNotesAction,
        }"
      >
        <HoverClickPopup
          v-if="displayClientNotesAction"
          :text="booking.clientNotes"
          @popup-toggle="updateShowNotes"
        />
        <BaseDropdownMenu
          v-if="dropdownItems.length > 0"
          :data-button="`booking-options-${booking.id}`"
          v-bind="buttonConfig"
          :menu-items="dropdownItems"
          tertiary
          :disable-list-item="disableMenuItem"
          @duplicateBooking="duplicateBooking"
          @editBookingDay="editBookingDay"
          @editBookingBlock="editBookingBlock"
          @cancelBooking="openCancelBookingDialog"
          @showFeedback="openFeedback"
          @menu-state-changed="updateShowNotes"
        />
      </div>
    </td>

    <!-- Approve/Decline (desktop) -->
    <td
      v-else-if="!isMobile && canApprovePendingBooking"
      style="text-align: end"
      class="ApprovalRequired-col"
      :class="[{ 'show-notes': showNotes }, { 'selected-row': selected }]"
    >
      <UnapprovedBookingButtons
        :booking="booking"
        class="approved-cell"
        @reloadBookingList="reloadBookingList"
      />
    </td>

    <!-- Right dots (mobile) -->
    <td
      v-if="isMobile && currentTabName !== BookingListTabKey.approvalRequired"
      class="right-dots"
    >
      <v-icon small @click="openBookingOptions">mdi-dots-vertical</v-icon>
    </td>
    <td v-else-if="isMobile && canApprovePendingBooking" class="right-cols">
      <UnapprovedBookingButtons
        :booking="booking"
        @reloadBookingList="reloadBookingList"
      />
    </td>
    <!-- Bottom sheet Feedback & Cancel in Mobile view -->
    <BookingOptionsSheet
      v-model="isBookingOptions"
      :current-tab-name="currentTabName"
      :is-past-booking="isPastBooking"
      :is-upcoming-booking="isUpcomingBooking"
      :is-valid-duplicate-status="isValidDuplicateStatus"
      :edit-booking-options="editBookingOptions"
      @editBookingDay="editBookingDay"
      @editBookingBlock="editBookingBlock"
      @duplicateBooking="duplicateBooking"
      @feedback="openFeedback"
      @cancelBooking="openCancelBookingDialog"
    />
    <BookingFeedbackModal
      v-model="feedbackDialog"
      :booking="booking"
      @feedbackProvided="handleFeedbackProvided"
    />
    <CancelBookingDialog
      v-if="showCancelBookingDialog"
      :show="showCancelBookingDialog"
      :booking="bookingToCancel"
      @closeModal="closeCancelBookingDialog"
      @closeModalAndRefresh="closeCancelBookingDialogAndRefresh"
    />
  </tr>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';
@import '@/styles/bookingList/booking-row.scss';
</style>
